import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup';
import { NormalContainerWithHeader } from '../../shared/components/student/NormalComponent'
import { Button, Grid2, Typography } from '@mui/material';
import { useCourseApartmentDetail, useGetDiscountCouponCodeByAuth, useStudentCourseByZoneList } from '../../assets/api/useAdminApi';
import { TextFormField } from '../../shared/components/formfields/TextFormField';
import { DateFormField } from '../../shared/components/formfields/DataFormField';
import CustomDivider from '../../shared/utils/CustomDivider';
import { Grid_Gap } from '../../shared/constants';
import useDiscountCouponCodeStore from '../../store/useDiscountCouponCodeStore';
import { IdAutoCompleteField } from '../../shared/components/formfields/IdAutoCompleteField';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToaster } from '../../shared/utils/ToasterProvider';
import useAuthStore from '../../store/useAuthStore';
import { useEffect, useState } from 'react';
import TableFilterTemplate from '../../shared/utils/TableFilterTemplate';
import api from '../../assets/nodeApi/axiosInstance';

const DiscountCouponForm = () => {
    const [course, setCourse] = useState("");
    const [filterCourseId, setFilterCourseId] = useState("");
    const { user, token } = useAuthStore()
    const queryClient = useQueryClient();
    const { showToaster } = useToaster();
   
    const [filters, setFilters] = useState({})
    const { data: CouponeCodeList, isLoading } = useGetDiscountCouponCodeByAuth(filters)
   
    const { data: courseList, isLoading: isCourseLoading } = useStudentCourseByZoneList();
    const CourseOption = courseList?.data?.map(course => ({
        label: course?.course_name,
        value: course?.course_name,
        ...course
    })) || [];
   
    const { discountCouponCode, clearDiscountCouponCode, setDiscountCouponCode } = useDiscountCouponCodeStore();
    useEffect(() => {
        setCourse(CourseOption.find((item) => item?.id === discountCouponCode?.course_id));
    }, [discountCouponCode]);
   
    const { data: courseApartmentDetail, isLoading: isCourseApartmentLoading } = useCourseApartmentDetail({ course_id: course?.id });
    const ApartmentOptions = courseApartmentDetail?.map(item => ({
        id: item?.apartments_id,
        label: item?.apartment_name,
        value: item?.apartment_name,
    }));
    
    const { data: filterCourseApartmentDetail, isLoading: isFilterCourseApartmentLoading } = useCourseApartmentDetail({ course_id: filterCourseId });
    const FilterApartmentOptions = filterCourseApartmentDetail?.map(item => ({
        id: item?.apartments_id,
        label: item?.apartment_name,
        value: item?.apartment_name,
    }));
   
    const initialValues = {
        course_id: discountCouponCode?.course_id || '',
        coupon_code: discountCouponCode?.coupon_code || '',
        start_date: discountCouponCode?.start_date || '',
        end_date: discountCouponCode?.end_date || '',
        discount_amount: discountCouponCode?.discount_amount || '',
        min_course_value: discountCouponCode?.min_course_value || '',
        apartment_id: discountCouponCode?.apartment_id || ''
    };

    const validationSchema = Yup.object({
        course_id: Yup.string().required('Course is required'),
        coupon_code: Yup.string().trim().required('Coupon code is required'),
        start_date: Yup.date()
            .required('Start date is required'),
        end_date: Yup.date()
            .required('End date is required'),
        discount_amount: Yup.number()
            .positive('Discount must be a positive value')
            .required('Discount amount is required'),
        min_course_value: Yup.number().required('Minimum course value is required'),
    });
    const mutation = useMutation({
        mutationFn: (formData) => {
            return discountCouponCode?.id
                ? api.put(`discounts/update/${discountCouponCode?.id}`, formData,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                )
                : api.post(`discounts/create`, formData,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
        },
        onSuccess: (response) => {

            showToaster('Success', response?.data?.message, 'success')
            queryClient.invalidateQueries({ queryKey: ['discounts/get-all-discounts'] });
            clearDiscountCouponCode()

        },
        onError: (err) => {

            showToaster(err?.response?.data?.message || "Error Caught While submiting the form", 'Error', 'error');
       
        },
    });
    const handleSubmit = (values, { setSubmitting, resetForm }) => {
        mutation.mutate(values, {
            onSettled: () => {
                setSubmitting(false);
            },
            onSuccess: () => {
                resetForm();
            }
        });

    }
    const mutationApprove = useMutation({
        mutationFn: ({ formData, id }) =>
            api.put(`discounts/status-update/${id}`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }),
    });
    const handleApprove = (id) => {
        const body = { status: "approved" };

        mutationApprove.mutate(
            { formData: body, id },
            {
                onSuccess: (response) => {
                    showToaster("Success", response?.data?.message, "success");
                    queryClient.invalidateQueries({
                        queryKey: ["discounts/get-all-discounts-details"],
                    });
                },
                onError: () => {
                    showToaster("Something Went wrong", "Error", "error");
                },
            }
        );
    };


    const couponCodeColumn = [
        {
            field: "sr_no",
            headerName: "Sr No",
            width: 100,

            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>

        },
        {
            field: "course_title",
            headerName: "Course",
            width: 150,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        {
            field: "apartment_name",
            headerName: "Apartment Name",
            width: 150,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        {
            field: "coupon_code",
            headerName: "Coupon Code",
            width: 150,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        {
            field: "start_date",
            headerName: "Start Date",
            width: 150,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        {
            field: "end_date",
            headerName: "End Date",
            width: 150,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        {
            field: "discount_amount",
            headerName: "Discount Amount",
            width: 150,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },


        {
            field: "min_course_value",
            headerName: "Min Course Value",
            width: 150,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },


        {
            field: "created_by",
            headerName: "Created By",
            width: 150,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        {
            field: "approved_by",
            headerName: "Approved By",
            width: 150,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        {
            field: "status",
            headerName: "Status",
            width: 150,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        // {
        //     field: "actions",
        //     type: "actions",
        //     headerName: "Actions",
        //     width: 200,
        //     cellClassName: "actions",
        //     renderCell: (params) => (
        //         <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
        //             <Tooltip title="Edit">
        //                 <GridActionsCellItem
        //                     key={0}
        //                     icon={<EditIcon />}
        //                     label="Edit"
        //                     onClick={() => {

        //                         setDiscountCouponCode(params?.row);
        //                         window.scrollTo({ top: 0, behavior: 'smooth' });

        //                     }}
        //                 />
        //             </Tooltip>
        //         </Box>
        //     ),
        // },
        ...((user.role === "Marketing Manager")
            ? [
                {
                    field: "approve",
                    headerName: "Approve",
                    width: 200,
                    renderCell: (params) => (
                        <Button variant='contained' onClick={() => handleApprove(params?.row?.id)} disabled={params.row.status !== "pending"}>
                            Approve
                        </Button>
                    ),
                },

            ]
            : [])

    ];

    return (
        <>
            <NormalContainerWithHeader headerText={"Discount Coupon Form"}>
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize={true}
                >
                    {({ resetForm, values }) => (
                        <Form>
                            <Grid2 container spacing={5}>
                                <Grid2 children size={{ xs: 12, sm: 4 }}>
                                    <Field
                                        name="course_id"
                                        component={IdAutoCompleteField}
                                        options={CourseOption}
                                        isLoading={isCourseLoading}
                                        label="Course"
                                        isRequired={true}
                                        onValueChange={(value) => {
                                            setCourse(value)
                                        }}
                                    />
                                </Grid2>
                                {
                                    !course?.online && <Grid2 children size={{ xs: 12, sm: 4 }}>
                                        <Field
                                            name="apartment_id"
                                            component={IdAutoCompleteField}
                                            options={ApartmentOptions}
                                            label="Apartment Name"
                                            isRequired={true}
                                            isLoading={isFilterCourseApartmentLoading}
                                        />

                                    </Grid2>
                                }

                                <Grid2 children size={{ xs: 12, sm: 4 }}>
                                    <Field
                                        name="coupon_code"
                                        component={TextFormField}
                                        label="Coupon code"
                                        isRequired={true}
                                        uppercase={true}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, sm: 4 }}>
                                    <Field
                                        name="start_date"
                                        component={DateFormField}
                                        label="Start Date"
                                        maxDate={values.end_date || undefined}
                                        minDate={new Date()}

                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, sm: 4 }}>
                                    <Field
                                        name="end_date"
                                        component={DateFormField}
                                        label="End Date"
                                        minDate={values.start_date || undefined || new Date()}

                                    />
                                </Grid2>
                                <Grid2 children size={{ xs: 12, sm: 4 }}>
                                    <Field
                                        name="discount_amount"
                                        component={TextFormField}
                                        label="Discount Amount"
                                        isRequired={true}
                                        type="number"
                                    />
                                </Grid2>
                                <Grid2 children size={{ xs: 12, sm: 4 }}>
                                    <Field
                                        name="min_course_value"
                                        component={TextFormField}
                                        label="Minimum Course Value"
                                        isRequired={true}
                                        type="number"
                                    />
                                </Grid2>
                            </Grid2>
                            <CustomDivider />
                            <Grid2 container spacing={Grid_Gap}>
                                <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                                    <Button fullWidth variant="outlined" onClick={() => { resetForm(); clearDiscountCouponCode() }} > Clear </Button>
                                </Grid2>
                                <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                                    <Button fullWidth variant="contained" type="submit"> {Object.keys(discountCouponCode).length === 0 ? "Submit" : "Update"}</Button>
                                </Grid2>
                            </Grid2>
                        </Form>
                    )}
                </Formik>
            </NormalContainerWithHeader>
            <CustomDivider />

            <TableFilterTemplate
                title="Coupon Code List"
                columns={couponCodeColumn}
                rowsData={CouponeCodeList?.data || []}
                isLoading={isLoading}
                filterInitialValues={filters}
                filterReset={() => setFilters({})}
                onApplyFilters={(f) => setFilters(f)}
                renderFilterFields={({ values }) => (
                    <>
                        <Grid2 children size={{ xs: 12 }}>
                            <Field
                                name="course_id"
                                component={IdAutoCompleteField}
                                options={CourseOption}
                                isLoading={isCourseLoading}
                                label="Course"
                                isRequired={true}
                                onValueChange={(value) => {
                                    setFilterCourseId(value?.id)
                                }}

                            />
                        </Grid2>
                        <Grid2 children size={{ xs: 12 }}>
                            <Field
                                name="apartment_id"
                                component={IdAutoCompleteField}
                                options={FilterApartmentOptions}
                                label="Apartment Name"
                                isRequired={true}
                                isLoading={isCourseApartmentLoading}
                            />
                        </Grid2>
                        <Grid2 children size={12}>
                            <Field
                                name="from_date"
                                component={DateFormField}
                                label="Start Date"
                                maxDate={values?.to || undefined}

                            />
                        </Grid2>
                        <Grid2 children size={12}>
                            <Field
                                name="to_date"
                                component={DateFormField}
                                label="End Date"
                                minDate={values?.from || undefined}

                            />
                        </Grid2>
                    </>
                )}
            />


        </>

    )
}

export default DiscountCouponForm